import { Subject } from 'rxjs';

import {
    AUTHENTICATED,
    REFRESHING,
    UNAUTHENTICATED,
} from '@perpay-web/authentication/constants/states';
import {
    requestAuthenticate,
    requestAuthenticateMFA,
    requestAuthenticatePasskey,
    requestLogout,
    requestMarketplacePartnerHostedSignup,
    requestPartnerHostedSignup,
    requestPasskeyAuthenticateBegin,
    requestPasskeyRegisterBegin,
    requestPasskeyRegisterComplete,
    requestPaychexHostedSignup,
    requestRefresh,
    requestResendMFA,
    requestSignup,
    requestSignupRequirements,
} from '@perpay-web/authentication/requests';
import {
    getAccessToken,
    getAvailableSpendingLimit,
    getDecodedTokens,
    getFirstName,
    getIsAuthenticated,
    getIsPartnerOnboarded,
    getIsRefreshing,
    getIsTokenExpired,
    getLastName,
    getMaxPays,
    getPartnerOnboardedCode,
    getPhoneNumber,
    getRefreshToken,
    getShowPartnerVerifyModal,
    getSignupRequirements,
    getUserUuid,
} from '@perpay-web/authentication/selectors';
import { noop } from '@perpay-web/utils/noop';
import { wrapInflight } from '@perpay-web/utils/promiseUtils';
import { decodeTokens, retrieveToken } from '@perpay-web/utils/tokenUtils';

type Token = {
    payload?: unknown;
    token?: string;
};

type AuthState = {
    access: Token;
    refresh: Token;
    tokenState: string;
    mfaRequired?: boolean;
};

const getInitialState = (): AuthState => {
    const storedToken = retrieveToken() || {};
    const tokenState = getIsTokenExpired(storedToken.refresh)
        ? UNAUTHENTICATED
        : AUTHENTICATED;

    return {
        access: storedToken.access,
        refresh: storedToken.refresh,
        tokenState,
    };
};

const getNextTokenObservableValue = (
    previousState: AuthState,
    nextState: AuthState,
) => ({
    previousState,
    nextState,
});

/**
 * The AuthenticationService maintains the source of truth for authentication state.
 * It is responsible for managing authentication through signup, login, refresh and logout.
 * This function returns an instance of the service. Only one instance of the service
 * should be created in an application, though multiple can be created in appropriate circumstances
 * i.e. for testing.
 */
export const createAuthenticationService = () => {
    /**
     * Setup the service internal state
     */
    const token$ = new Subject();
    let state = getInitialState();

    const setState = (updatedState: Partial<AuthState>) => {
        const previousState = state;
        const nextState = { ...state, ...updatedState };
        state = nextState;
        token$.next(getNextTokenObservableValue(previousState, nextState));
    };

    const refresh = wrapInflight(() => {
        setState({
            tokenState: REFRESHING,
        });

        const refreshToken = getRefreshToken(state);

        const handleSuccess = (tokens: { access?: Token }) => {
            if (!tokens.access) {
                setState({
                    tokenState: UNAUTHENTICATED,
                });
                return tokens;
            }
            setState({
                access: tokens.access,
                tokenState: AUTHENTICATED,
            });
            return tokens;
        };
        // If the refresh request fails, we should not log out, because the request could be failing
        // because the page is navigating, and not because there was a server-side error.
        const handleError = noop;

        return requestRefresh(refreshToken).then(handleSuccess, handleError);
    });

    const getRefreshedValue = (getter: () => unknown) => {
        if (!getIsTokenExpired(state.access)) {
            return Promise.resolve(getter());
        }

        if (getIsTokenExpired(state.refresh)) {
            return Promise.reject(
                new Error('Refresh token expired. Cannot refresh.'),
            );
        }

        return refresh().then(() => getter());
    };

    const getRefreshedAccessToken = (): Promise<string> =>
        getRefreshedValue(() => getAccessToken(state));

    const getRefreshedEmail = () =>
        getRefreshedValue(() => getDecodedTokens(state).access.payload.email);

    const signup = (accountData: unknown) =>
        // @ts-ignore
        requestSignup(accountData).then((results) => {
            const { tokens, ...rest } = results;
            const decodedTokens = decodeTokens(tokens);
            setState({
                access: decodedTokens.access,
                refresh: decodedTokens.refresh,
                tokenState: AUTHENTICATED,
            });
            return rest;
        });

    const partnerHostedSignup = (userData: unknown) =>
        requestPartnerHostedSignup(userData).then((results) => {
            const { tokens, ...rest } = results;
            const decodedTokens = decodeTokens(tokens);
            setState({
                access: decodedTokens.access,
                refresh: decodedTokens.refresh,
                tokenState: AUTHENTICATED,
            });
            return rest;
        });

    const marketplacePartnerHostedSignup = (userData: unknown) =>
        requestMarketplacePartnerHostedSignup(userData).then((results) => {
            const { tokens, ...rest } = results;
            const decodedTokens = decodeTokens(tokens);
            setState({
                access: decodedTokens.access,
                refresh: decodedTokens.refresh,
                tokenState: AUTHENTICATED,
            });
            return rest;
        });

    const paychexHostedSignup = (userData: unknown) =>
        requestPaychexHostedSignup(userData).then((results) => {
            const { tokens, ...rest } = results;
            const decodedTokens = decodeTokens(tokens);
            setState({
                access: decodedTokens.access,
                refresh: decodedTokens.refresh,
                tokenState: AUTHENTICATED,
            });
            return rest;
        });

    const authenticate = (credentials: unknown) =>
        requestAuthenticate(credentials).then((decodedTokens) => {
            if (decodedTokens.mfa && decodedTokens.mfa.mfaRequired) {
                setState({
                    tokenState: UNAUTHENTICATED,
                });
            }

            if (decodedTokens.access && decodedTokens.refresh) {
                setState({
                    access: decodedTokens.access,
                    refresh: decodedTokens.refresh,
                    tokenState: AUTHENTICATED,
                });
            }

            // could be access and refresh tokens, or MFA token
            return decodedTokens;
        });

    const authenticateMFA = (payload: unknown) =>
        requestAuthenticateMFA(payload).then((decodedTokens) => {
            if (!decodedTokens.access || !decodedTokens.refresh) {
                setState({
                    tokenState: UNAUTHENTICATED,
                });
            } else {
                setState({
                    access: decodedTokens.access,
                    refresh: decodedTokens.refresh,
                    tokenState: AUTHENTICATED,
                });
            }

            return decodedTokens;
        });

    const authenticatePasskey = (credentials: unknown) =>
        requestAuthenticatePasskey(credentials).then((decodedTokens) => {
            if (decodedTokens.access && decodedTokens.refresh) {
                setState({
                    access: decodedTokens.access,
                    refresh: decodedTokens.refresh,
                    tokenState: AUTHENTICATED,
                });
            }
            return decodedTokens;
        });

    const passkeyAuthenticateBegin = () => requestPasskeyAuthenticateBegin();

    const passkeyRegisterBegin = () =>
        getRefreshedAccessToken().then((accessToken) =>
            requestPasskeyRegisterBegin(accessToken),
        );

    const passkeyRegisterComplete = (payload: unknown) =>
        getRefreshedAccessToken().then((accessToken) =>
            requestPasskeyRegisterComplete(payload, accessToken),
        );

    const logout = wrapInflight(() => {
        const promise =
            state.tokenState === UNAUTHENTICATED
                ? Promise.resolve()
                : requestLogout(getRefreshToken(state));

        const setLogoutState = () => {
            setState({
                access: {},
                refresh: {},
                tokenState: UNAUTHENTICATED,
            });
        };

        return promise.then(setLogoutState, setLogoutState);
    });

    const fetchSignupRequirements = wrapInflight(() =>
        getRefreshedAccessToken().then((accessToken) =>
            requestSignupRequirements(accessToken),
        ),
    );

    return {
        token$: token$.asObservable(),
        signup,
        partnerHostedSignup,
        marketplacePartnerHostedSignup,
        paychexHostedSignup,
        authenticate,
        authenticateMFA,
        authenticatePasskey,
        passkeyAuthenticateBegin,
        passkeyRegisterBegin,
        passkeyRegisterComplete,
        refresh,
        logout,
        getRefreshedAccessToken: () => getRefreshedAccessToken(),
        getRefreshedEmail: () => getRefreshedEmail(),
        getDecodedTokens: () => getDecodedTokens(state),
        getAccessToken: () => getAccessToken(state),
        getIsAccessTokenExpired: () => getIsTokenExpired(state.access),
        getIsRefreshTokenExpired: () => getIsTokenExpired(state.refresh),
        getIsAuthenticated: () => getIsAuthenticated(state),
        getIsPartnerOnboarded: () => getIsPartnerOnboarded(state),
        getPartnerOnboardedCode: () => getPartnerOnboardedCode(state),
        getPhoneNumber: () => getPhoneNumber(state),
        getFirstName: () => getFirstName(state),
        getLastName: () => getLastName(state),
        getSignupRequirements: () => getSignupRequirements(state),
        getShowPartnerVerifyModal: () => getShowPartnerVerifyModal(state),
        getMaxPays: () => getMaxPays(state),
        getAvailableSpendingLimit: () => getAvailableSpendingLimit(state),
        resendMFA: (payload: unknown) => requestResendMFA(payload),
        getIsRefreshing: () => getIsRefreshing(state),
        getUserUuid: () => getUserUuid(state),
        fetchSignupRequirements,
    };
};
